import { useSyncExternalStore } from 'react';
import { inQuietAudience, isQuietSender } from '../../shared/constants';
import type { DailyPostDoc, MessageDoc, UserDoc } from '../../shared/types';

/**
 * The test account, hidden from the family entirely.
 *
 * Quiet messages keep its chat traffic between the two test phones. This goes
 * the rest of the way: outside the quiet audience the account is not a member
 * anyone can see. Its face, its GiviSnaps, its comments and its poll votes are
 * dropped, and its uid is scrubbed out of everybody else's reactions, seen
 * lists and snap opens, so no count includes it and no row says "Someone".
 *
 * Only the uid is hidden, and only once the member list has said which uid the
 * test email belongs to -- which can be after the first posts arrive. So the
 * hooks scrub on the way out and re-run when this set changes, rather than
 * the readers scrubbing once on the way in.
 */
export type Hidden = ReadonlySet<string>;

const NONE: Hidden = new Set();

/** The uids to hide from this viewer: none at all for the quiet audience. */
export function hiddenUidsOf(users: Record<string, UserDoc>, viewerEmail: string | null | undefined): Hidden {
  if (inQuietAudience(viewerEmail)) return NONE;
  const hidden = Object.entries(users)
    .filter(([, user]) => isQuietSender(user.email))
    .map(([uid]) => uid);
  return hidden.length ? new Set(hidden) : NONE;
}

export function withoutHiddenUsers(users: Record<string, UserDoc>, hidden: Hidden): Record<string, UserDoc> {
  if (!hidden.size) return users;
  return Object.fromEntries(Object.entries(users).filter(([uid]) => !hidden.has(uid)));
}

/** A uid-keyed map with the hidden keys taken out. The same object when none are in it. */
function withoutKeys<V>(map: Record<string, V> | undefined, hidden: Hidden): Record<string, V> | undefined {
  if (!map || !Object.keys(map).some((uid) => hidden.has(uid))) return map;
  return Object.fromEntries(Object.entries(map).filter(([uid]) => !hidden.has(uid)));
}

function withoutUids(list: string[] | undefined, hidden: Hidden): string[] | undefined {
  if (!list || !list.some((uid) => hidden.has(uid))) return list;
  return list.filter((uid) => !hidden.has(uid));
}

/**
 * Chat rows: the test account's own dropped, and its reactions and snap opens
 * taken off everyone else's. Rows it never touched come back as they were, so
 * nothing memoised on them re-renders for nothing.
 */
export function scrubMessages<T extends { data: MessageDoc }>(rows: T[], hidden: Hidden): T[] {
  if (!hidden.size) return rows;
  return rows
    .filter((row) => !hidden.has(row.data.authorUid))
    .map((row) => {
      const likes = withoutKeys(row.data.likes, hidden);
      const viewedBy = withoutUids(row.data.viewedBy, hidden);
      if (likes === row.data.likes && viewedBy === row.data.viewedBy) return row;
      return {
        ...row,
        data: { ...row.data, likes: likes ?? {}, ...(viewedBy ? { viewedBy } : {}) },
      };
    });
}

/** GiviSnaps: the same, over reactions and seenBy. */
export function scrubPosts<T extends { data: DailyPostDoc }>(rows: T[], hidden: Hidden): T[] {
  if (!hidden.size) return rows;
  return rows
    .filter((row) => !hidden.has(row.data.authorUid))
    .map((row) => {
      const reactions = withoutKeys(row.data.reactions, hidden);
      const seenBy = withoutUids(row.data.seenBy, hidden);
      if (reactions === row.data.reactions && seenBy === row.data.seenBy) return row;
      return {
        ...row,
        data: { ...row.data, reactions: reactions ?? {}, seenBy: seenBy ?? [] },
      };
    });
}

/** Comments under a GiviSnap, or anything else with an author. */
export function scrubAuthored<T extends { data: { authorUid: string } }>(rows: T[], hidden: Hidden): T[] {
  if (!hidden.size) return rows;
  return rows.filter((row) => !hidden.has(row.data.authorUid));
}

/** Poll votes, keyed by voter. */
export function scrubVotes<V>(votes: Record<string, V>, hidden: Hidden): Record<string, V> {
  return withoutKeys(votes, hidden) ?? votes;
}

/*
 * The current set, for the hooks. Written by useUsers each time the member
 * list changes; everything else only reads it.
 */
let current: Hidden = NONE;
const listeners = new Set<() => void>();

export function setHidden(next: Hidden): void {
  const same = next.size === current.size && [...next].every((uid) => current.has(uid));
  if (same) return;
  current = next;
  for (const listener of listeners) listener();
}

export function useHidden(): Hidden {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}
